<template>
  <div class="flex flex-col">
    <div v-if="options.length > 5" class="px-2 pt-2 pb-1 border-b dark:border-gray-800">
      <UInput
        v-model="search"
        :placeholder="$t('general.filter')"
        icon="i-lucide-search"
        variant="none"
        size="sm"
        autofocus
      />
    </div>

    <div class="max-h-80 overflow-y-auto p-1">
      <template v-for="(group, gi) in filteredGroups" :key="gi">
        <div
          v-if="group.label"
          class="px-2 pt-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
        >
          {{ group.label }}
        </div>

        <button
          v-for="option in group.options"
          :key="optionKey(option)"
          type="button"
          class="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
          @click="toggle(option)"
        >
          <UCheckbox
            :model-value="isSelected(option)"
            size="sm"
            class="pointer-events-none"
          />

          <span
            v-if="option.avatar?.url"
            class="size-5 rounded-full bg-cover bg-center flex-shrink-0"
            :style="{ backgroundImage: `url(${option.avatar.url})` }"
          />
          <span
            v-else-if="option.avatar?.initials"
            class="size-5 rounded-full bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300 flex items-center justify-center text-[10px] font-medium flex-shrink-0"
          >
            {{ option.avatar.initials.charAt(0).toUpperCase() }}
          </span>
          <UIcon
            v-else-if="option.icon"
            :name="resolveIconName(option.icon) ?? option.icon"
            class="size-4 text-gray-500 dark:text-gray-400 flex-shrink-0"
          />

          <span class="text-sm truncate flex-grow">{{ option.label }}</span>

          <span
            v-if="getCount(option) !== undefined"
            class="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0"
          >
            {{ formatCount(getCount(option)!) }}
          </span>
        </button>
      </template>

      <div
        v-if="!filteredGroups.length"
        class="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
      >
        {{ $t('general.noResults') }}
      </div>

      <!-- Linear-style footer: how many options have a zero count (and so
           are hidden from the list above). Only rendered when `optionCount`
           is wired up AND at least one option is filtered out. -->
      <div
        v-if="zeroCountCount > 0"
        class="px-3 py-2 border-t dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5"
      >
        <UIcon name="i-lucide-info" class="size-3.5" />
        {{ $t('components.elements.filters.notMatching', { count: zeroCountCount }) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { resolveIconName } from '@/components/elements/dataGrid/utils'

import { groupOptions, matchesSearch, type OptionGroup } from './utils'

interface Props {
  options: any[]
  modelValue: any[]
  /** Optional per-option count provider (e.g. "107 issues"). */
  optionCount?: (option: any) => number | undefined
}

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  modelValue: () => [],
  optionCount: undefined,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any[]): void
}>()

const search = ref('')

const groups = computed<OptionGroup[]>(() => groupOptions(props.options ?? []))

const getCount = (option: any): number | undefined => {
  return props.optionCount ? props.optionCount(option) : undefined
}

/**
 * Strip options whose count provider returns 0 — they're not selectable in a
 * useful way, so Linear-style we hide them from the visible list and surface
 * the dropped-count in the footer instead. Options with `undefined` count
 * (no count provider, or provider explicitly returning undefined) stay
 * visible since we can't tell either way.
 */
const isZeroCount = (option: any): boolean => getCount(option) === 0

const visibleGroups = computed<OptionGroup[]>(() => {
  return groups.value
    .map((g) => ({ label: g.label, options: g.options.filter((o) => !isZeroCount(o)) }))
    .filter((g) => g.options.length > 0)
})

const zeroCountCount = computed<number>(() => {
  if (!props.optionCount) return 0
  let n = 0
  for (const g of groups.value) for (const o of g.options) if (isZeroCount(o)) n++
  return n
})

const filteredGroups = computed<OptionGroup[]>(() => {
  if (!search.value) return visibleGroups.value
  return visibleGroups.value
    .map((g) => ({
      label: g.label,
      options: g.options.filter((o) => matchesSearch(o.label, search.value)),
    }))
    .filter((g) => g.options.length > 0)
})

const formatCount = (n: number): string => {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`
  return String(n)
}

const optionKey = (option: any): string | number => {
  const v = option.value
  if (v == null) return option.label ?? ''
  if (typeof v === 'object') return v.id ?? v.idServiceCategory ?? JSON.stringify(v)
  return v
}

// Selection uses identity comparison when option values are objects (the
// existing convention — see useServiceFilters' category filter), and
// equality otherwise.
const isSelected = (option: any): boolean => {
  return (props.modelValue ?? []).some((v: any) => {
    if (v === option.value) return true
    if (v && option.value && typeof v === 'object' && typeof option.value === 'object') {
      return optionKey({ value: v }) === optionKey(option)
    }
    return false
  })
}

const toggle = (option: any) => {
  const current = props.modelValue ?? []
  if (isSelected(option)) {
    emit('update:modelValue', current.filter((v: any) => !(v === option.value || (
      v && option.value && typeof v === 'object' && typeof option.value === 'object'
      && optionKey({ value: v }) === optionKey(option)
    ))))
  } else {
    emit('update:modelValue', [...current, option.value])
  }
}
</script>
